/**
 * CollaborationPanelWrapper Component
 * Wrapper for Live Collaboration panel with remote update handling
 */

import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { CollaborationPanel } from '../collaboration';
import { toast } from 'sonner';

export const CollaborationPanelWrapper = ({
  isOpen,
  onClose,
  collabSessionId,
  user,
  isPlaying,
  audioContextRef,
  playAll,
  stopAll,
  setCurrentTime,
  setBpm,
  setTimeSignature,
  setRemoteCursors,
  setCollabUsers,
  updateTrack,
  handleLeaveSession,
}) => {
  const handleUserCursorMove = (usrId, cursor) => {
    setRemoteCursors(prev => ({
      ...prev,
      [usrId]: cursor
    }));
    // Clear cursor after 5 seconds of inactivity
    setTimeout(() => {
      setRemoteCursors(prev => {
        const newCursors = { ...prev };
        if (newCursors[usrId]?.x === cursor?.x && newCursors[usrId]?.y === cursor?.y) {
          delete newCursors[usrId];
        }
        return newCursors;
      });
    }, 5000);
  };

  const handleUsersUpdate = (users) => {
    setCollabUsers(users || []);
  };

  const handleRemoteUpdate = (data) => {
    
    // Handle transport sync
    if (data.type === 'transport_update') {
      const { action, position } = data;
      if (action === 'play') {
        if (position !== undefined) {
          setCurrentTime(position);
        }
        if (!isPlaying) {
          setTimeout(() => {
            if (audioContextRef?.current) {
              playAll();
            }
          }, 50);
        }
        toast.info(`${data.userName || 'A collaborator'} started playback`);
      } else if (action === 'pause' || action === 'stop') {
        if (isPlaying) {
          stopAll();
        }
        if (action === 'stop' && position !== undefined) {
          setCurrentTime(position);
        }
        toast.info(`${data.userName || 'A collaborator'} ${action === 'stop' ? 'stopped' : 'paused'} playback`);
      }
      return;
    }
    
    // Handle BPM changes
    if (data.type === 'bpm_changed' || data.type === 'bpm_change_applied') {
      const newBpm = data.bpm || data.operation?.data?.bpm;
      if (newBpm) {
        setBpm(newBpm);
        toast.info(`BPM changed to ${newBpm} by ${data.userName || data.operation?.userName || 'a collaborator'}`);
      }
    // Handle Time Signature changes
    } else if (data.type === 'time_signature_changed' || data.type === 'time_signature_change_applied') {
      const newTimeSignature = data.timeSignature || data.operation?.data?.timeSignature;
      if (newTimeSignature) {
        setTimeSignature(newTimeSignature);
        toast.info(`Time signature changed to ${newTimeSignature}/4 by ${data.userName || data.operation?.userName || 'a collaborator'}`);
      }
    } else if ((data.type === 'track_updated' || data.type === 'track_update_applied') && (data.trackId || data.operation?.data?.trackId)) {
      const trackId = data.trackId || data.operation?.data?.trackId;
      const changes = data.changes || data.operation?.data?.changes;
      if (trackId && changes) updateTrack(trackId, changes);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && collabSessionId && (
        <div className="fixed top-0 right-0 bottom-0 z-40">
          <CollaborationPanel
            projectId={collabSessionId}
            userId={user?.id || `guest-${Date.now()}`}
            userName={user?.name || user?.email?.split('@')[0] || 'Guest User'}
            onUserCursorMove={handleUserCursorMove}
            onUsersUpdate={handleUsersUpdate}
            onRemoteUpdate={handleRemoteUpdate}
            onClose={onClose}
            onLeaveSession={handleLeaveSession}
            isVisible={isOpen}
          />
        </div>
      )}
    </AnimatePresence>
  );
};

export default CollaborationPanelWrapper;
